/*
#*===================================================================
#*
#* Licensed Materials - Property of GameTimeFootage Inc.
#*
#* Copyright NextPro 2018. All Rights Reserved.
#*
#*===================================================================
*/

import { Injectable, QueryList } from '@angular/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { filter, map, mergeMap, switchMap, take } from 'rxjs/operators';
import dayjs from 'dayjs';

import { environment } from 'environments/environment';

import { Constants } from 'app/shared/models/constants.model';
import * as fromSharedStore from 'app/shared/store';
import { ProfileService } from 'app/profile/services/profile.service';
import { StorageService } from 'app/shared/services/storage.service';
import { AssetsService, UtilsService } from 'app/shared/services';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ProductDTO } from '@shared/models/product.model';
import { GetBreakdownQueueState } from '@library/store/reducers/get-breakdown-queue.reducer';

@Injectable()
export class LibraryService {
  private readonly HRR_TIP_KEY = 'hrr_tip_viewed';

  constructor(
    private httpClient: HttpClient,
    private sharedStore: Store<fromSharedStore.SharedState>,
    private assetsService: AssetsService,
    private profileService: ProfileService,
    private storageService: StorageService,
    private utils: UtilsService
  ) {}

  /**
   * Returns the video history for current user
   *
   * @returns {Observable<any>}
   */
  getVideoHistory(limit: number) {
    return this.handleGetResponse(
      this.httpClient.get(`${environment.apiHostname}/v1/user-actions/list/played/assets?limit=${limit}`)
    );
  }

  /**
   * Clears the video history for current user
   *
   * @returns {Observable<any>}
   */
  clearVideoHistory() {
    return this.handleOperationResponse(
      this.httpClient.delete(`${environment.apiHostname}/v1/user-actions/clear/played/assets`)
    );
  }

  /**
   * Returns the downloads for current user
   *
   * @returns {Observable<any>}
   */
  getDownloads() {
    return this.handleGetResponse(
      this.httpClient.get(`${environment.apiHostname}/v1/user-actions/list/downloaded/assets`)
    );
  }

  /**
   * Returns the favorites for current user
   *
   * @returns {Observable<any>}
   */
  getFavorites() {
    return this.handleGetResponse(this.httpClient.get(`${environment.apiHostname}/v1/user-actions/like/list/assets`));
  }

  /**
   * Returns the users/athletes following current user
   *
   * @returns {Observable<any>}
   */
  getFollowing() {
    return this.sharedStore.select(fromSharedStore.getUserData).pipe(
      filter((userState) => userState.success),
      take(1),
      mergeMap((userState: any) => {
        return this.profileService.getAthleteFollowing(userState.userData.id).pipe(
          map((resp) => {
            return resp;
          })
        );
      })
    );
  }

  /**
   * Returns the highlight reels for current user
   *
   * @returns {Observable<any>}
   */
  getHighlights() {
    return this.sharedStore.select(fromSharedStore.getUserData).pipe(
      filter((userState) => userState.success),
      take(1),
      mergeMap((userState: any) => {
        // list of inviter IDs or current user ID
        const whosHRs = [userState.userData.id];
        // get Highlight reels for Ids
        return forkJoin(whosHRs.map((userId) => this.getAthleteHighlightReelsRequests(userId))).pipe(
          map((hrLists) => {
            // flatten array of results
            return hrLists.reduce((acc: any[], list: any) => acc.concat(list), []);
          })
        );
      })
    );
  }

  getHighlightReelsForPurchases(purchaseIds: number[]): Observable<any> {
    if (!purchaseIds.length) {
      return of([]);
    }

    return this.handleGetResponse(
      this.httpClient.get(
        `${
          environment.apiHostname
        }/v1/highlight-reels/list/highlight-reels/by/purchase-ids?purchaseIds=${purchaseIds.join(',')}`
      )
    );
  }

  getHighlightReelById(id: number): Observable<any> {
    if (!id) {
      return of(null);
    }

    return this.handleGetResponse(this.httpClient.get(`${environment.apiHostname}/v1/highlight-reels/${id}`));
  }

  getEstimatedDelivery(highlightReel) {
    return this.sharedStore.select(fromSharedStore.getAccessState).pipe(
      filter((accessState) => accessState.success),
      take(1),
      map((access) => {
        const correspondingPurchase = access.products.find((product) => product.id === highlightReel?.purchase?.id);
        if (
          [Constants.SPORTS.VOLLEYBALL, Constants.SPORTS.BASKETBALL].includes(
            correspondingPurchase.sport?.name?.toLowerCase()
          )
        ) {
          return dayjs(highlightReel.submittedAt).add(Constants.HR_DAYS_TO_DELIVER.VOLLEY_BASKETBALL, 'day');
        }
        if (
          highlightReel.purchase &&
          this.getHrType(highlightReel.purchase.type) === Constants.HIGHLIGHT_REEL_TYPES.PRO
        ) {
          return dayjs(highlightReel.submittedAt).add(Constants.HR_DAYS_TO_DELIVER.PRO, 'day');
        } else {
          return dayjs(highlightReel.submittedAt).add(Constants.HR_DAYS_TO_DELIVER.STANDARD, 'day');
        }
      })
    );
  }

  /**
   * Returns the custom videos for current user
   *
   * @returns {Observable<any>}
   */
  getCustomVideos() {
    return this.sharedStore.select(fromSharedStore.getUserData).pipe(
      filter((userState) => userState.success),
      take(1),
      mergeMap((userState: any) => {
        return this.getAthleteCustomVideos(userState.userData.id);
      })
    );
  }

  /**
   * Returns the clips for current user
   *
   * @returns {Observable<any>}
   */
  getLibraryClips() {
    const userData$ = this.sharedStore.select(fromSharedStore.getUserData).pipe(
      filter((userState) => userState.success),
      map((userState) => userState.userData),
      take(1)
    );
    const access$ = this.sharedStore.select(fromSharedStore.getAccessState).pipe(
      filter((accessState) => accessState.success),
      take(1)
    );
    return combineLatest([userData$, access$]).pipe(
      mergeMap(([userData, accessState]) => {
        const myId = userData.id;
        const inviteesIds = accessState.athletes.map((athlete) => athlete.id);
        const parentIds = accessState.parents.map((parent) => parent.id);

        return this.assetsService.getClipsForUsers([myId, ...parentIds, ...inviteesIds]);
      })
    );
  }

  /**
   * Returns the library highlights videos for current user
   *
   * @returns {Observable<any>}
   */
  getLibraryHighlightsCustomVideos() {
    // get current user
    return this.sharedStore.select(fromSharedStore.getUserData).pipe(
      filter((userState) => !userState.loading),
      map((userState) => userState.userData),
      take(1),
      switchMap((userState) => {
        // always return the user so that we don't loose the user data

        // user not logged in
        if (!userState?.id) {
          return combineLatest([of(userState), of(null)]);
        }

        return combineLatest([
          of(userState),
          this.sharedStore.select(fromSharedStore.getAccessState).pipe(
            filter((accessState) => accessState.success),
            take(1)
          ),
        ]);
      }),
      mergeMap(([userData, accessState]) => {
        if (!userData?.id) {
          return of([]);
        }

        const myId = userData.id;
        const inviteesIds = accessState.athletes.map((athlete) => athlete.id);
        const parentIds = accessState.parents.map((parent) => parent.id);

        return this.assetsService.getLibraryHighlightsForUsers([myId, ...parentIds, ...inviteesIds]);
      })
    );
  }

  /**
   * Returns the highlight reels for current user
   *
   * @returns {Observable<any>}
   */
  getLibraryHighlightReels() {
    // get current user
    return this.sharedStore.select(fromSharedStore.getUserData).pipe(
      filter((userState) => !userState.loading),
      map((userState) => userState.userData),
      take(1),
      switchMap((userState) => {
        // user not logged in
        if (!userState?.id) {
          return of(null);
        }

        return this.sharedStore.select(fromSharedStore.getAccessState).pipe(
          filter((accessState) => accessState.success),
          take(1)
        );
      }),
      mergeMap((accessState) => {
        if (!accessState) {
          return of([]);
        }
        const hrPurchases = accessState.products.filter(this.utils.purchaseIncludesHR).map((purchase) => purchase.id);
        return this.getHighlightReelsForPurchases(hrPurchases);
      })
    );
  }

  /**
   * Returns the shared assets for current user
   */
  getSharedAssets() {
    return this.handleGetResponse(this.httpClient.get(`${environment.apiHostname}/v1/user-actions/list/shared-assets`));
  }

  /**
   * Returns the clips created by the current user
   */
  getSharedClips() {
    return this.handleGetResponse(this.httpClient.get(`${environment.apiHostname}/v1/user-actions/list/shared-clips`));
  }

  /**
   * Returns the shared assets for current user
   *
   * @returns {Observable<any>}
   */
  getGameFilm(playerUsername: string, playerEmail: string) {
    return this.httpClient.post(`${environment.apiHostname}/v1/games/search`, { playerUsername, playerEmail }).pipe(
      map((resp: any) => {
        if (resp.result.success) {
          const games: any[] = resp.data;
          return games
            .map((game) => {
              const assets = game.assets.filter((assetT) => assetT.type === Constants.ASSET_TYPE.FULL_GAME_VIDEO);
              const asset = assets.length > 0 ? assets[0] : null;
              let video: any = null;
              if (asset) {
                video = {
                  ...asset,
                  event: game.event,
                  date: game.date,
                  field: game.field,
                  location: game.location,
                  teams: game.teams,
                };
              }
              return video;
            })
            .filter((item) => item);
        }
        return [];
      })
    );
  }

  /**
   * Returns the game films based on purchase ids
   *
   * @returns {Observable<any>}
   */
  getGameFilms(purchaseIds: number[]) {
    return this.httpClient.post(`${environment.apiHostname}/v1/assets/game-films`, { purchaseIds }).pipe(
      map((resp: any) => {
        if (resp.result.success && resp.data?.length) {
          return resp.data;
        }
        return [];
      })
    );
  }

  /**
   * Returns the saved game films based on college coach id
   *
   * @returns {Observable<any>}
   */
  getSavedGameFilms(userId: number) {
    return this.httpClient.post(`${environment.apiHostname}/v1/assets/saved-game-films`, { userId }).pipe(
      map((resp: any) => {
        if (resp.result.success && resp.data?.length) {
          return resp.data;
        }
        return [];
      })
    );
  }

  /**
   * Submit highlight form
   *
   * @returns {Observable<any>}
   */
  submitHighlightForm(data: any) {
    for (const playInfo of data.playsInfo) {
      playInfo.event = playInfo.eventId > 0 ? { id: playInfo.eventId } : null;
      playInfo.game = playInfo.gameId > 0 ? { id: playInfo.gameId } : null;
      playInfo.clip =
        playInfo.clip && playInfo.clip.id ? playInfo.clip : playInfo.clipId > 0 ? { id: playInfo.clipId } : null;
    }
    const highlightReelsSaveData = {
      ...data.athleteInfo,
      highlightPlays: data.playsInfo.filter(
        (play) =>
          play.type !== Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES.ADD &&
          play.type !== Constants.HIGHLIGHT_REEL_SELECT_PLAY_TYPES.SELECT_CLIP_UPLOAD
      ),
      highlightCustomPlays: data.playsInfo.filter(
        (play) =>
          play.type === Constants.HIGHLIGHT_REEL_PRO_PLAY_TYPES.ADD ||
          play.type === Constants.HIGHLIGHT_REEL_SELECT_PLAY_TYPES.SELECT_CLIP_UPLOAD
      ),
      purchase: data.purchase,
      submittedAt: data.submittedAt,
      isExpeditedDelivery: data.isExpeditedDelivery,
      hrTitle: data.hrTitle,
      hrNotes: data.hrNotes,
    };
    if (highlightReelsSaveData.id > 0) {
      return this.httpClient
        .put(`${environment.apiHostname}/v1/highlight-reels/${highlightReelsSaveData.id}`, highlightReelsSaveData)
        .pipe(
          map((resp: any) => {
            return resp;
          })
        );
    } else {
      return this.httpClient.post(`${environment.apiHostname}/v1/highlight-reels`, highlightReelsSaveData).pipe(
        map((resp: any) => {
          return resp;
        })
      );
    }
  }

  /**
   * Submit highlight form vold old
   *
   * @returns {Observable<any>}
   */
  submitHighlightFormV2(data: any) {
    const serviceCallsObservableArray = [];

    for (const playInfo of data.playsInfo) {
      playInfo.event = { id: playInfo.eventId };
      playInfo.game = { id: playInfo.gameId };
      if (playInfo.id) {
        serviceCallsObservableArray.push(
          this.httpClient.put(`${environment.apiHostname}/v1/highlight-play/${playInfo.id}`, playInfo)
        );
      } else {
        serviceCallsObservableArray.push(
          this.httpClient.post(`${environment.apiHostname}/v1/highlight-play`, playInfo)
        );
      }
    }

    return forkJoin(serviceCallsObservableArray).pipe(
      switchMap((highlightsRespArray: any[]) => {
        const hrrArray: any[] = [];
        // tslint:disable-next-line:prefer-for-of
        for (let i = 0; i < highlightsRespArray.length; ++i) {
          hrrArray.push(
            highlightsRespArray[i].data.data ? highlightsRespArray[i].data.data : highlightsRespArray[i].data
          );
        }
        const highlightReelsPostData = { ...data.athleteInfo, highlightPlays: hrrArray };
        if (highlightReelsPostData.id > 0) {
          return this.httpClient
            .put(`${environment.apiHostname}/v1/highlight-reels/${highlightReelsPostData.id}`, highlightReelsPostData)
            .pipe(
              map((resp: any) => {
                return resp;
              })
            );
        } else {
          return this.httpClient.post(`${environment.apiHostname}/v1/highlight-reels`, highlightReelsPostData).pipe(
            map((resp: any) => {
              return resp;
            })
          );
        }
      })
    );
  }

  // tslint:disable-next-line:cognitive-complexity
  getAthleteHighlightReelsRequests(id: any) {
    return forkJoin([
      this.httpClient.get(`${environment.apiHostname}/v1/highlight-reels/list/highlight-reels/${id}`),
      this.profileService.getAthletePrivateAssets(id, Constants.ASSET_TYPE.ATHLETE_HIGHLIGHT_VIDEO),
    ]).pipe(
      map(([resp, privateAssets]: [any, any[]]) => {
        if (!resp.result.success) {
          throw new Error(resp.result.message);
        }

        const hrrData: any[] = resp.data.sort(
          (a, b) => dayjs(a.createdAt).toDate().getTime() - dayjs(b.createdAt).toDate().getTime()
        );

        let draftIndex = 1;
        for (const hrr of hrrData) {
          if (hrr.asset) {
            hrr.asset.private = privateAssets.filter((a) => a.id === hrr.asset.id).length > 0;
          } else if (hrr.draft === true) {
            hrr.draftIndex = draftIndex++;
          }
        }
        return hrrData;
      })
    );
  }

  getAthleteCustomVideos(id: any) {
    return this.assetsService.getCustomAssets(id).pipe(
      map((customAssets) => {
        const customVideoData: any[] = customAssets && customAssets[0] ? customAssets[0].customAssets : [];
        return customVideoData;
      })
    );
  }

  getIsHRRTipViewed(userId) {
    const tipValue = this.storageService.getLocalStorageItem(`${this.HRR_TIP_KEY}-${userId}`);
    return !!tipValue;
  }

  setIsHRRTipViewed(userId) {
    this.storageService.setLocalStorageItem(`${this.HRR_TIP_KEY}-${userId}`, '1');
  }

  getHrType(purchaseType: string) {
    if (purchaseType === Constants.PURCHASE_TYPE.PRO_HR || purchaseType === Constants.PURCHASE_TYPE.ONE_EVENT_GFHRP) {
      return Constants.HIGHLIGHT_REEL_TYPES.PRO;
    }
    return Constants.HIGHLIGHT_REEL_TYPES.SELECT;
  }

  deleteHrPlay(playId: number) {
    return this.handleOperationResponse(
      this.httpClient.delete(`${environment.apiHostname}/v1/highlight-play/${playId}`)
    );
  }

  deleteHrCustomPlay(playId: number) {
    return this.handleOperationResponse(
      this.httpClient.delete(`${environment.apiHostname}/v1/highlight-custom-play/${playId}`)
    );
  }

  getUserNotifications(userId: number, type?) {
    if (!userId) {
      return of([]);
    }

    const params = !type ? { take: '20', skip: '0' } : { take: '20', skip: '0', type };

    return this.handleGetResponse(this.httpClient.get(`${environment.apiHostname}/v1/user-notification`, { params }));
  }

  dismissNotification(notificationId: number) {
    if (!notificationId) {
      return;
    }
    return this.handleGetResponse(
      this.httpClient.delete(`${environment.apiHostname}/v1/user-notification/${notificationId}`)
    );
  }

  getGameNotifications(products: ProductDTO[]) {
    const userProducts = products
      .filter(
        (product) =>
          product.event &&
          product.type !== Constants.PURCHASE_TYPE.HR &&
          product.type !== Constants.PURCHASE_TYPE.PRO_HR &&
          product.type !== Constants.PURCHASE_TYPE.PRO_FEEDBACK &&
          product.type !== Constants.PURCHASE_TYPE.BREAKDOWN &&
          product.type !== Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME &&
          product.type !== Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME_TP &&
          product.type !== Constants.PURCHASE_TYPE.CHANNEL_GF &&
          product.type !== Constants.PURCHASE_TYPE.ALL_EVENTS_2018
      )
      .sort((productA, productB) => {
        return dayjs(productA.event.startDate).isSameOrBefore(productB.event.startDate) ? 1 : -1;
      });

    const uniqueEvents = new Set();
    // get game will be available notifications
    const gamesNotActive = userProducts.reduce((acc, product) => {
      if (!product.event.published && !uniqueEvents.has(product.event.id)) {
        uniqueEvents.add(product.event.id);
        acc.push({
          ...product.event,
          purchase: product.id,
          notificationType: Constants.INLINE_NOTIFICATION_TYPES.GAME_FILM_NOT_READY,
        });
      }
      return acc;
    }, []);

    uniqueEvents.clear();
    const teamNotificationEvents = userProducts.reduce((acc, product) => {
      if (product.event.published && !product.teamId && !uniqueEvents.has(product.event.id)) {
        uniqueEvents.add(product.event.id);
        acc.push({
          ...product.event,
          isAthleteSport: this.utils.isAthleteSport(product?.sport?.name, product?.event?.type),
          purchase: product.id,
          notificationType: Constants.INLINE_NOTIFICATION_TYPES.GAME_FILM_READY,
        });
      }
      return acc;
    }, []);

    return [...teamNotificationEvents, ...gamesNotActive];
  }

  getBreakdownNotifications(products: ProductDTO[], breakdownState: GetBreakdownQueueState) {
    const userProducts = products
      .filter(
        (product) =>
          product.event &&
          product.type !== Constants.PURCHASE_TYPE.HR &&
          product.type !== Constants.PURCHASE_TYPE.PRO_HR &&
          product.type !== Constants.PURCHASE_TYPE.PRO_FEEDBACK &&
          product.addons?.length &&
          product.addons?.some(
            (addon) =>
              addon.type === Constants.PURCHASE_TYPE.BREAKDOWN ||
              addon.type === Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME ||
              addon.type === Constants.PURCHASE_TYPE.BREAKDOWN_ONE_GAME_TP
          )
      )
      .sort((productA, productB) => {
        return dayjs(productA.event.startDate).isSameOrBefore(productB.event.startDate) ? 1 : -1;
      });

    const uniqueEvents = new Set();
    // get game will be available notifications
    const gamesNotActive = userProducts.reduce((acc, product) => {
      if (!product.event.published && !uniqueEvents.has(product.event.id)) {
        uniqueEvents.add(product.event.id);
        acc.push({
          ...product.event,
          eventName: product.event?.name,
          purchase: product.id,
          notificationType: Constants.INLINE_NOTIFICATION_TYPES.BREAKDOWN_GAME_NOT_READY,
        });
      }
      return acc;
    }, []);

    const gamesWoBreakdown = (breakdownState.ids as (number | string)[]).reduce((acc, id: number | string) => {
      const bq = breakdownState.entities[id];
      if (!bq.tagQueue?.published && bq.game?.event?.publishDate) {
        acc.push({
          ...bq.game?.event,
          name: `${bq.game?.teams?.[0].name} vs ${bq.game?.teams?.[1].name}`,
          eventName: bq.game?.event?.name,
          submittedAt: bq.submittedAt,
          estimatedDeliveryAt: dayjs(bq.submittedAt).utc().add(Constants.BREAKDOWN_DAYS_TO_DELIVER, 'day'),
          gameId: bq.game?.id,
          purchase: bq.purchase?.id,
          notificationType: Constants.INLINE_NOTIFICATION_TYPES.GAME_WO_BREAKDOWN,
        });
      }
      return acc;
    }, []);

    return [...gamesNotActive, ...gamesWoBreakdown];
  }

  public openCloseDropdowns(elements: QueryList<NgSelectComponent>, openLast: boolean) {
    if (openLast && elements?.first?.isOpen) {
      elements.first.close();
      elements.last?.open();
    }
    if (!openLast && elements?.last?.isOpen) {
      elements.last.close();
      elements.first?.open();
    }
  }

  private loadCurrentUserInvites(privateForUsers, currentUser) {
    return this.sharedStore.select(fromSharedStore.getUserInvitesState).pipe(
      map((state) => {
        // invites for current user not loaded
        if (!state.success && !state.failed && !state.loading) {
          this.sharedStore.dispatch(new fromSharedStore.GetUserInvites(currentUser.id));
          return {
            loading: true,
            allowed: null,
          };
        }

        // invites loaded
        // if user is in list
        if (
          privateForUsers.some((privateUser) => state.userData.some((invitedUser) => invitedUser.id === privateUser.id))
        ) {
          return {
            loading: false,
            allowed: true,
          };
        }

        // if user is not allowed
        return {
          loading: false,
          allowed: false,
        };
      }),
      filter((state) => {
        return !state.loading;
      }),
      take(1)
    );
  }

  /**
   * Generic method handler for when getting a response from server for a GET operation
   *
   * @returns {Observable<any>}
   */
  private handleGetResponse(methodCallResponse: Observable<object>) {
    return methodCallResponse.pipe(
      map((response: any) => {
        if (response.result.success) {
          return response.data;
        } else {
          throw new Error(response.result.message);
        }
      })
    );
  }

  /**
   * Generic method handler for when getting a response from server for a PUT/DELETE/POST altering operation
   *
   * @returns {Observable<any>}
   */
  private handleOperationResponse(methodCallResponse: Observable<object>) {
    return methodCallResponse.pipe(
      map((response: any) => {
        if (response.result.success) {
          return response.result.success;
        } else {
          throw new Error(response.result.message);
        }
      })
    );
  }

  // private getResponseData(observable: Observable<any>, error: string) {
  //   return observable.pipe(
  //     map((response: ServerResponseModel) => {
  //       if (response.result.success) {
  //         return response.data;
  //       } else {
  //         throw new Error(error);
  //       }
  //     })
  //   );
  // }
}
